/**
 * Endless mode: cycles the whole scenario catalogue forever, running each
 * through the same stream machine the chat uses, and checking the shared
 * invariants after each run. Pass and fail counts accumulate until the mode
 * is stopped.
 *
 * The machine is created directly, not through useChat, because endless mode
 * is a headless test runner that happens to live in the browser. The point is
 * that it exercises the exact same stream-core code the chat runs, so a pass
 * here is a pass about the code the user sees.
 *
 * Settings changes take effect on the next cycle, not the current one, so
 * adjusting delayMs or client resilience mid run does not restart the loop.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  assertMonotonicIds,
  assertNoDuplicatedTokens,
  assertNoGapsUnlessMarked,
  assertTerminalReached,
  createStreamMachine,
  type StreamPhase,
  type StreamState,
} from '@harness/stream-core';
import { createRandom } from '@harness/protocol';

import { browserTimers, now, random } from '../lib/environment.ts';
import { createFetchTransport } from '../transport/fetch-sse.ts';
import { buildOverrides, type HarnessSettings } from './use-settings.ts';
import type { ScenarioInfo } from './use-chat.ts';

const MAX_RESULTS = 100;

export interface EndlessResult {
  scenario: string;
  pass: boolean;
  phase: StreamPhase;
  reason?: string;
  durationMs: number;
  reconnects: number;
  framesApplied: number;
}

export interface EndlessController {
  running: boolean;
  pass: number;
  fail: number;
  current: string | null;
  results: EndlessResult[];
  start: () => void;
  stop: () => void;
}

export function useEndless(
  scenarios: ScenarioInfo[],
  settings: HarnessSettings,
): EndlessController {
  const [running, setRunning] = useState(false);
  const [pass, setPass] = useState(0);
  const [fail, setFail] = useState(0);
  const [current, setCurrent] = useState<string | null>(null);
  const [results, setResults] = useState<EndlessResult[]>([]);

  const runningRef = useRef(false);
  const indexRef = useRef(0);

  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const scenariosRef = useRef(scenarios);
  scenariosRef.current = scenarios;

  const stop = useCallback(() => {
    runningRef.current = false;
    setRunning(false);
  }, []);

  const start = useCallback(() => {
    if (runningRef.current) return;
    runningRef.current = true;
    setRunning(true);
    setPass(0);
    setFail(0);
    setResults([]);
    indexRef.current = 0;

    void (async () => {
      while (runningRef.current) {
        const scs = scenariosRef.current;
        if (scs.length === 0) break;

        const info = scs[indexRef.current % scs.length];
        indexRef.current += 1;
        if (info === undefined) break;
        setCurrent(info.id);

        const s = settingsRef.current;
        const overrides = buildOverrides(s, info.id, info.knobs);
        const startedAt = now();
        let result: EndlessResult;

        try {
          const machine = createStreamMachine({
            transport: createFetchTransport(),
            streamUrl: '/v1/chat/stream',
            resumeUrl: '/v1/chat/resume',
            body: {
              conversationId: 'endless',
              message: `run ${info.id}`,
              scenario: info.id,
              overrides,
            },
            timers: browserTimers,
            now,
            random: s.client.seededJitter ? createRandom(s.randomizer.seed) : random,
            maxReconnectAttempts: s.client.maxReconnectAttempts,
            byteTimeoutMs: s.client.byteTimeoutMs,
            contentTimeoutMs: s.client.contentTimeoutMs,
          });

          const finalState: StreamState = await machine.start().catch(() => machine.state);

          assertTerminalReached(finalState);
          assertNoDuplicatedTokens(finalState);
          assertMonotonicIds(finalState.timeline);
          assertNoGapsUnlessMarked(finalState);

          result = {
            scenario: info.id,
            pass: true,
            phase: finalState.phase,
            durationMs: Math.round(now() - startedAt),
            reconnects: finalState.reconnects,
            framesApplied: finalState.timeline.filter((e) => e.kind === 'frame').length,
          };
          setPass((p) => p + 1);
        } catch (error) {
          result = {
            scenario: info.id,
            pass: false,
            phase: 'error',
            reason: error instanceof Error ? error.message : String(error),
            durationMs: Math.round(now() - startedAt),
            reconnects: 0,
            framesApplied: 0,
          };
          setFail((f) => f + 1);
        }

        setResults((prev) => {
          const next = [...prev, result];
          return next.length > MAX_RESULTS ? next.slice(next.length - MAX_RESULTS) : next;
        });

        await new Promise<void>((resolve) => {
          setTimeout(resolve, s.endless.delayMs);
        });
      }

      runningRef.current = false;
      setRunning(false);
      setCurrent(null);
    })();
  }, []);

  useEffect(() => {
    if (settings.endless.enabled) {
      start();
    } else {
      stop();
    }
  }, [settings.endless.enabled, start, stop]);

  return { running, pass, fail, current, results, start, stop };
}
